/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import MonsterOpen5e from 'model/formats/MonsterOpen5e';
import CreatureOpen5e from 'model/formats/CreatureOpen5e';
import Class from 'model/character/Class';

// Environment name normalization: maps each non-canonical name to its preferred
// replacement (or '' to drop it entirely).
const ENV_SWITCH_MAP = new Map<string, string>([
    ['Abyss',         'The Abyss'],
    ['Caverns',       'Caves'],
    ['Coastal',       'Coast'],
    ['Grassland',     'Grassland or Plains'],
    ['Hill',          'Hills'],
    ['Mountain',      'Mountains'],
    ['Ruin',          'Ruins'],
    ['Arctic',        'Arctic or Tundra'],
    ['Tundra',        'Arctic or Tundra'],
    ['Swamp',         'Swamp or Marsh'],
    ['Forest',        'Forest or Jungle'],
    ['Jungle',        'Forest or Jungle'],
    ['Plane Of Air',  'Plane of Air'],
    ['Plane Of Earth','Plane of Earth'],
    ['Plane Of Fire', 'Plane of Fire'],
    ['Plane Of Water','Plane of Water'],
    ['Settlement',    'Urban'],
    ['Underdark',     'Underworld'],
    ['Ice',           ''],
    ['Volcano',       ''],
]);

// Pluralize select singular environment names ('Hill' → 'Hills', etc.) and sort.
export function cleanupEnvironments(envs: string[]): string[] {
    return [...new Set(envs
        .map(o => ENV_SWITCH_MAP.has(o) ? ENV_SWITCH_MAP.get(o)! : o)
        .filter(Boolean))].sort();
}

// Per-monster fixups for known data errors in v1 /monsters payloads.
// Mutates and returns the input.
export function fixMonsterErrors(m: MonsterOpen5e): MonsterOpen5e {
    switch (m.slug) {
         case 'phoenixborn-sorcerer':
             if (m.senses === 'xx, passive Perception xx')
                m.senses = '';
             break;
    }
    m.environments = cleanupEnvironments(m.environments || []);
    return m;
}

// Per-creature fixups for known data errors in v2 /creatures payloads.
// Mutates and returns the input.
export function fixCreatureErrors(c: CreatureOpen5e): CreatureOpen5e {
    switch (c.key) {
        case 'srd_donkey':
            if (!c.hit_dice)
                c.hit_dice = '2d8 + 2';
            break;
    }
    const envStrings = (c.environments || []).map(e => typeof e === 'string' ? e : (e?.name ?? String(e)));
    c.environments = cleanupEnvironments(envStrings);
    return c;
}

// Per-class fixups for known data errors in /classes payloads.
// Mutates and returns the input.
export function fixClassErrors(pcClass: Class): Class {
    if (!pcClass.desc) return pcClass;
    if (pcClass.name === "Soulspy" || pcClass.name === 'Underfoot') {
        // 'Spells Unknown' and wrong column count in divider row
        pcClass.desc = pcClass.desc.replace("| Rouge Level  | Cantrips Known | Spells Unknown | 1st | 2nd | 3rd | 4th |",
                                            "| Rouge Level  | Cantrips Known | Spells known | 1st | 2nd | 3rd | 4th |");
        pcClass.desc = pcClass.desc.replace("|--------------|----------------|----------------------|-----|-----|-----| ",
                                            "|--------------|----------------|--------------|-----|-----|-----|-----| ");
    }

    // fix issue of headers not being on their own line:
    pcClass.desc = pcClass.desc.replace(/([\.a-mo-z] *)##/g, '$1\n###');

    if (pcClass.name === 'Blood Domain') {
        // remove this table, because it's not formatted well, doesn't list its minimum level, and only causes confusion
        pcClass.desc = pcClass.desc.replace(
            " **Health State Examples**\n"+
            " | 100%    | Untouched            | \n"+
            " | 99%-50% | Injured              | \n"+
            " | 49%-1%  | Heavily Wounded      | \n"+
            " | 0%      | Unconscious or Dying | \n"+
            " | –       | Dead                 |\n\n",
            "");
    }
    if (pcClass.name === 'Bard') {
        // Bard's column headers are switched
        pcClass.table = pcClass.table?.replace('Spells Known | Cantrips Known', 'Cantrips Known | Spells Known');
    }
    // Keep the naming of 'Spells Known' consistent:
    pcClass.table = pcClass.table?.replace('| Known Spells |', '| Spells Known |');
    return pcClass;
}
